import { useCallback, useEffect, useState } from "react";
import "./App.css";
import TMGClaimSystem from "./TMGClaimSystem";
import MaintenanceAdmin from "./MaintenanceAdmin";
import MaintenancePage from "./MaintenancePage";
import { fetchStatus, getMaintenanceToken } from "./services/maintenanceApi";

const MAINTENANCE_BYPASS_STORAGE_KEY = "tmgMaintenanceBypass";
const PREVIEW_STORAGE_KEY = "tmgMaintenancePreview";
const STATUS_POLL_MS = 30000;

// Both spellings work, with or without a trailing slash.
const ADMIN_PATHS = ["/mode/maintanance", "/mode/maintenance"];

function isAdminRoute() {
  const path = window.location.pathname.replace(/\/+$/, "").toLowerCase();
  return ADMIN_PATHS.includes(path);
}

function readSession(key) {
  try {
    return sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeSession(key, value) {
  try {
    if (value) sessionStorage.setItem(key, "true");
    else sessionStorage.removeItem(key);
  } catch {
    /* storage blocked */
  }
}

function hasBypassRight() {
  const params = new URLSearchParams(window.location.search);
  const bypassKey = process.env.REACT_APP_MAINTENANCE_BYPASS_KEY;

  if (bypassKey && params.get("maintenance_bypass") === bypassKey) {
    writeSession(MAINTENANCE_BYPASS_STORAGE_KEY, true);
    return true;
  }

  // Whoever is signed into the control page can keep using the site while it is down.
  if (getMaintenanceToken()) return true;

  return readSession(MAINTENANCE_BYPASS_STORAGE_KEY);
}

/** Tells the admin the site is down for everyone else, and lets them see what visitors see. */
function BypassBanner({ onPreview }) {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 9999,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        padding: "8px 16px",
        backgroundColor: "#111827",
        color: "#fff",
        fontSize: "13px",
        fontFamily: "'DM Sans', Arial, sans-serif",
      }}
    >
      <span>
        <strong>Maintenance mode is ON.</strong> Visitors see the maintenance page - you are getting
        through because you are signed into Maintenance Control.
      </span>
      <button
        type="button"
        onClick={onPreview}
        style={{
          background: "transparent",
          border: "1px solid rgba(255,255,255,0.5)",
          color: "#fff",
          borderRadius: "6px",
          padding: "3px 10px",
          fontSize: "12px",
          cursor: "pointer",
        }}
      >
        See what visitors see
      </button>
      <a href="/mode/maintanance" style={{ color: "#FFD200", fontSize: "12px" }}>
        Maintenance Control
      </a>
    </div>
  );
}

function App() {
  const forcedMaintenance = process.env.REACT_APP_MAINTENANCE_MODE === "true";
  const adminRoute = isAdminRoute();

  const [status, setStatus] = useState({ checked: false, isDown: false, message: null });
  const [preview, setPreview] = useState(() => readSession(PREVIEW_STORAGE_KEY));

  useEffect(() => {
    if (adminRoute) return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        const data = await fetchStatus();
        if (!cancelled) {
          setStatus({ checked: true, isDown: Boolean(data.is_down), message: data.message });
        }
      } catch {
        // If the switch cannot be reached, leave the site up.
        if (!cancelled) setStatus((prev) => ({ ...prev, checked: true }));
      }
    };

    check();
    const timer = setInterval(check, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [adminRoute]);

  const startPreview = useCallback(() => {
    writeSession(PREVIEW_STORAGE_KEY, true);
    setPreview(true);
  }, []);

  const stopPreview = useCallback(() => {
    writeSession(PREVIEW_STORAGE_KEY, false);
    setPreview(false);
  }, []);

  if (adminRoute) {
    return <MaintenanceAdmin />;
  }

  const down = forcedMaintenance || status.isDown;

  if (down && (!hasBypassRight() || preview)) {
    return (
      <>
        <MaintenancePage message={status.message} />
        {preview && (
          <button
            type="button"
            onClick={stopPreview}
            style={{
              position: "fixed",
              bottom: "16px",
              right: "16px",
              zIndex: 10000,
              background: "#111827",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "8px 14px",
              fontSize: "13px",
              cursor: "pointer",
              fontFamily: "'DM Sans', Arial, sans-serif",
            }}
          >
            Exit preview
          </button>
        )}
      </>
    );
  }

  return (
    <>
      {down && <BypassBanner onPreview={startPreview} />}
      <TMGClaimSystem />
    </>
  );
}

export default App;
